import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Paper, AppBar, Toolbar, Typography, IconButton,
  Avatar, Alert, Chip, Button, Snackbar, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import TimerIcon from '@mui/icons-material/Timer';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PhoneIcon from '@mui/icons-material/Phone';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallEndIcon from '@mui/icons-material/CallEnd';
import SwapCallsIcon from '@mui/icons-material/SwapCalls';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useConsultation } from '../../contexts/ConsultationContext';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import consultationService from '../../services/consultationService';
import api from '../../services/api';
import {
  MessageList, MessageInput, ConnectionStatus, SessionTimer
} from '../chat';
import FloatingJitsiPiP from '../call/FloatingJitsiPiP';
import RatingModal from '../consultation/RatingModal';
import PreCallDobModal from '../consultation/PreCallDobModal';
import VedicToolsOverlay from '../consultation/VedicToolsOverlay';
import RechargeModal from '../wallet/RechargeModal';
import { useCall } from '../../contexts/CallContext';
import useCallLayout from '../../hooks/useCallLayout';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import useOfflineMessageQueue from '../../hooks/useOfflineMessageQueue';

const ChatWithAstrologerEnhanced = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const layout = useCallLayout();

  // From navigation state (on-demand sessions pass this)
  const locationState = location.state || {};
  const chatRoomId = locationState.chatRoomId;
  const ratePerMin = locationState.ratePerMin || 0;

  const {
    currentConsultation,
    getConsultationDetails,
    loading: consultationLoading,
    error: consultationError
  } = useConsultation();

  const {
    socket,
    isConnected,
    messages,
    typingUsers,
    error: chatError,
    connect,
    disconnect,
    sendMessage
  } = useChat();

  const { isOnline } = useNetworkStatus();

  const [astrologer, setAstrologer] = useState(null);
  const [localMessages, setLocalMessages] = useState([]);

  // On-demand billing state
  const [elapsed, setElapsed] = useState(0); // seconds since session started
  const [totalSpent, setTotalSpent] = useState(0);
  const [lowBalanceWarning, setLowBalanceWarning] = useState(false);
  const [sessionEndingWarning, setSessionEndingWarning] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [wallet75Warning, setWallet75Warning] = useState(null); // { balance, percentUsed }
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [connectionTimedOut, setConnectionTimedOut] = useState(false);
  const connectionTimeoutRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Active call type — mutable to support audio↔video switching
  const [activeCallType, setActiveCallType] = useState(null);
  const [activeRate, setActiveRate] = useState(ratePerMin);

  // Pre-call DOB modal + Vedic tools overlay + floating PiP
  const [showDobModal, setShowDobModal] = useState(false);
  const [showVedicOverlay, setShowVedicOverlay] = useState(false);
  const [pendingCallStart, setPendingCallStart] = useState(false);
  const { startCall, endCall: endCallContext, activeCall } = useCall();

  // Offline queue integration
  const sendViaSocketOrRest = useCallback(async (msg) => {
    if (isConnected && socket) {
      sendMessage({
        consultation_id: msg.consultationId,
        content: msg.content,
        client_message_id: msg.clientMessageId,
      });
      return true;
    } else {
      const response = await consultationService.sendMessage(msg.consultationId, msg.content);
      return !!response?.data;
    }
  }, [isConnected, socket, sendMessage]);

  const {
    queueMessage,
    retryMessage,
    queuedMessages,
  } = useOfflineMessageQueue({
    userId: user?.id?.toString() || '',
    sendMessage: sendViaSocketOrRest,
    getConsultationStatus: (id) => consultationService.getConsultationStatus(id),
    isOnline,
  });

  // Derive type and isInstant from navigation state OR consultation data (fallback)
  const rawCallType = locationState.type || currentConsultation?.type;
  const callType = activeCallType || rawCallType;
  const isInstant = locationState.isInstant ||
    (chatRoomId && chatRoomId.startsWith('instant_')) ||
    (currentConsultation?.chat_room_id && currentConsultation.chat_room_id.startsWith('instant_'));

  useEffect(() => {
    if (consultationId) loadConsultation();
  }, [consultationId]);

  useEffect(() => {
    if (currentConsultation && user) {
      connect(currentConsultation.id, user.id, null);
      if (currentConsultation.astrologer) setAstrologer(currentConsultation.astrologer);
    }
    return () => disconnect();
  }, [currentConsultation, user]);

  // Start billing elapsed timer for on-demand sessions
  useEffect(() => {
    if (!isInstant) return;
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsed(secs);
      setTotalSpent((secs / 60) * activeRate);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [isInstant, activeRate]);

  // Handle switching between audio and video call mid-session
  const handleSwitchCallType = async () => {
    const newType = callType === 'call' ? 'video' : 'call';
    const astro = astrologer || currentConsultation?.astrologer;
    const newRate = newType === 'video'
      ? (astro?.price_per_minute_video || 30)
      : (astro?.price_per_minute_call || 20);

    try {
      await api.put(`/consultations/${consultationId}/switch-type`, { type: newType });
    } catch (err) {
      console.warn('Backend switch-type not available, switching locally:', err.message);
    }
    setActiveCallType(newType);
    setActiveRate(newRate);
  };

  // PRD-010: 45-second connection timeout after astrologer accepts a call.
  // If Jitsi fails to load (containers down, external_api.js 404, WebRTC blocked),
  // this prevents the consultation from sitting in "connecting" forever.
  useEffect(() => {
    const isConnecting = isInstant && (callType === 'call' || callType === 'video') &&
      currentConsultation?.status === 'connecting' && !callEnded && !connectionTimedOut;
    if (isConnecting) {
      connectionTimeoutRef.current = setTimeout(() => {
        setConnectionTimedOut(true);
      }, 45000);
    }
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    };
  }, [isInstant, callType, currentConsultation?.status, callEnded, connectionTimedOut]);

  // On-demand socket events
  useEffect(() => {
    if (!socket || !isInstant) return;

    const onLowBalance = (data) => {
      if (data.consultationId == consultationId) setLowBalanceWarning(true);
    };
    const onSessionEnding = (data) => {
      if (data.consultationId == consultationId) setSessionEndingWarning(true);
    };
    const onEnded = (data) => {
      if (data.consultationId == consultationId) {
        clearInterval(timerRef.current);
        setCallEnded(true);
        setShowRatingModal(true);
      }
    };
    const onWallet75 = (data) => {
      if (data.consultationId == consultationId) {
        setWallet75Warning({ balance: data.balance, percentUsed: Math.round(data.percentUsed) });
      }
    };

    socket.on('low_balance_warning', onLowBalance);
    socket.on('session_ending_low_balance', onSessionEnding);
    socket.on('consultation_ended', onEnded);
    socket.on('wallet_75_warning', onWallet75);

    return () => {
      socket.off('low_balance_warning', onLowBalance);
      socket.off('session_ending_low_balance', onSessionEnding);
      socket.off('consultation_ended', onEnded);
      socket.off('wallet_75_warning', onWallet75);
    };
  }, [socket, isInstant, consultationId, navigate]);

  const loadConsultation = async () => {
    try {
      await getConsultationDetails(consultationId);
      await loadMessages();
    } catch (error) {
      console.error('Failed to load consultation:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await consultationService.getMessages(consultationId);
      const msgs = response?.data?.data?.messages || response?.data?.messages || [];
      setLocalMessages(msgs);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async (content) => {
    if (!content.trim() || !consultationId) return;

    if (!isOnline) {
      // OFFLINE: queue + pending UI
      const clientMessageId = crypto.randomUUID();
      const entry = queueMessage(consultationId, content.trim(), clientMessageId);
      if (entry) {
        setLocalMessages(prev => [...prev, {
          id: entry.id,
          client_message_id: entry.clientMessageId,
          content: entry.content,
          consultation_id: consultationId,
          sender_id: user?.id,
          sender_type: 'user',
          sent_at: entry.createdAt,
          message_type: 'text',
          delivery_status: null,
          is_queued: true,
          queue_status: 'pending',
        }]);
      }
    } else if (isConnected) {
      // ONLINE + SOCKET: normal path (unchanged behavior)
      sendMessage({ consultation_id: consultationId, content: content.trim() });
    } else {
      // ONLINE + NO SOCKET: REST fallback (unchanged behavior)
      try {
        const response = await consultationService.sendMessage(consultationId, content.trim());
        const newMessage = response?.data?.data?.message || response?.data?.message;
        if (newMessage) setLocalMessages(prev => [...prev, newMessage]);
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    }
  };

  const handleRetryMessage = useCallback((messageId) => {
    retryMessage(messageId);
    setLocalMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, queue_status: 'pending' } : msg
    ));
  }, [retryMessage]);

  // Merge server messages + local messages + queued messages for display
  const allMessages = useMemo(() => {
    const combined = [...messages, ...localMessages];
    const existingIds = new Set(combined.map(m => m.client_message_id).filter(Boolean));
    const newQueued = queuedMessages
      .filter(q => q.status !== 'sent' && !existingIds.has(q.clientMessageId))
      .map(q => ({
        id: q.id,
        client_message_id: q.clientMessageId,
        content: q.content,
        consultation_id: consultationId,
        sender_id: user?.id,
        sender_type: 'user',
        sent_at: q.createdAt,
        is_queued: true,
        queue_status: q.status === 'sending' ? 'processing' : q.status,
      }));
    return [...combined, ...newQueued].sort((a, b) =>
      new Date(a.sent_at) - new Date(b.sent_at)
    );
  }, [messages, localMessages, queuedMessages, consultationId, user?.id]);

  // Called by JitsiCall when videoConferenceJoined fires — clears connection timeout.
  const handleJitsiJoined = useCallback(() => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    setConnectionTimedOut(false);
  }, []);

  // Called when user clicks "Cancel" on the connection-failed dialog.
  const handleConnectionCancel = async () => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    clearInterval(timerRef.current);
    try {
      await api.post(`/consultations/${consultationId}/end`);
    } catch (err) {
      console.error('Failed to end consultation after connection timeout:', err);
    }
    navigate('/dashboard');
  };

  const handleEndSession = async () => {
    clearInterval(timerRef.current);
    try {
      await api.post(`/consultations/${consultationId}/end`);
    } catch (err) {
      console.error('End session error', err);
    }
    navigate('/dashboard');
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  if (!isAuthenticated) {
    return <Box p={3}><Alert severity="warning">Please login to access this chat</Alert></Box>;
  }
  if (consultationLoading) {
    return <Box p={3}><Typography>Loading consultation...</Typography></Box>;
  }
  if (consultationError) {
    return <Box p={3}><Alert severity="error">{consultationError}</Alert></Box>;
  }

  const astrologerName = astrologer?.name || astrologer?.user?.name || 'Astrologer';
  const isCallType = callType === 'call' || callType === 'video';

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')}>
            <BackIcon />
          </IconButton>

          {astrologer && (
            <>
              <Avatar src={astrologer.profile_image} sx={{ ml: 1, mr: 1 }} />
              <Box flex={1}>
                <Typography variant="h6">{astrologerName}</Typography>
                <Typography variant="caption">{astrologer.specialization}</Typography>
              </Box>
            </>
          )}

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {isInstant ? (
              <>
                {callType === 'call' && <Chip icon={<PhoneIcon />} label="Voice Call" size="small" color="primary" />}
                {callType === 'video' && <Chip icon={<VideocamIcon />} label="Video Call" size="small" color="secondary" />}
                {isCallType && !callEnded && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={callType === 'call' ? <VideocamIcon /> : <PhoneIcon />}
                    onClick={handleSwitchCallType}
                    sx={{ fontSize: '0.7rem', minWidth: 0, textTransform: 'none', borderColor: 'rgba(255,255,255,0.5)', color: 'inherit' }}
                  >
                    {callType === 'call' ? 'Video' : 'Voice'}
                  </Button>
                )}
                <Chip icon={<TimerIcon />} label={formatTime(elapsed)} size="small" />
                <Chip label={`₹${activeRate}/min`} size="small" color="warning" />
                <Chip
                  icon={<AccountBalanceWalletIcon />}
                  label={`₹${totalSpent.toFixed(0)} spent`}
                  size="small"
                  color="default"
                />
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<CallEndIcon />}
                  onClick={handleEndSession}
                >
                  End
                </Button>
              </>
            ) : (
              <>
                <ConnectionStatus isConnected={isConnected} error={chatError} />
                {currentConsultation?.start_time && (
                  <SessionTimer startTime={currentConsultation.start_time} duration={900} />
                )}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {chatError && <Alert severity="error" sx={{ m: 1 }}>{chatError}</Alert>}

      {/* Low balance warnings */}
      <Snackbar
        open={sessionEndingWarning}
        message="Very low balance! Session ending soon."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        action={
          <Button size="small" color="warning" onClick={() => setShowRechargeModal(true)}>
            Recharge Now
          </Button>
        }
      />
      <Snackbar
        open={lowBalanceWarning && !sessionEndingWarning}
        onClose={() => setLowBalanceWarning(false)}
        message="Low balance — less than 2 minutes remaining"
        autoHideDuration={6000}
        action={
          <Button size="small" color="inherit" onClick={() => setShowRechargeModal(true)}>
            Recharge
          </Button>
        }
      />

      {/* Floating Jitsi PiP — three modes: pip (floating), fullscreen, split.
          GUARD: If GlobalCallPiP (App.js-level) already has an active call,
          do NOT render a second FloatingJitsiPiP here — that would create a
          duplicate JitsiMeetExternalAPI connection (same room, same user),
          causing duplicate participants and broken audio.  See #343. */}
      {isInstant && isCallType && !callEnded && !activeCall && (
        <FloatingJitsiPiP
          consultationId={consultationId}
          chatRoomId={chatRoomId || currentConsultation?.chat_room_id}
          displayName={user?.name || 'User'}
          videoMuted={callType === 'call'}
          callType={callType}
          onEnd={handleEndSession}
          onJoined={handleJitsiJoined}
          onUserDetailsClick={() => setShowVedicOverlay(true)}
          onDashboardClick={() => navigate('/astrologer/dashboard')}
        >
          {/* Chat content passed as children for split-screen mode */}
          <>
            <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <MessageList
                messages={allMessages}
                currentUserId={user?.id}
                currentUserType="user"
                typingUsers={typingUsers}
              />
            </Box>
            <Box sx={{ p: 2 }}>
              <MessageInput
                onSendMessage={handleSendMessage}
                disabled={false}
                placeholder="Type your message..."
              />
            </Box>
          </>
        </FloatingJitsiPiP>
      )}

      {/* Chat area — visible in PiP mode (call floats on top) and when no active call */}
      {(!(isInstant && isCallType && !callEnded) || layout.mode === 'pip') && (
        <>
          <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <MessageList
              messages={allMessages}
              currentUserId={user?.id}
              currentUserType="user"
              typingUsers={typingUsers}
            />
          </Box>
          <Box sx={{ p: 2 }}>
            {!isConnected && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Socket not connected. Messages sent via REST.
              </Alert>
            )}
            <MessageInput
              onSendMessage={handleSendMessage}
              disabled={false}
              placeholder="Type your message..."
            />
          </Box>
        </>
      )}
      {/* 75% wallet usage warning dialog */}
      <Dialog
        open={!!wallet75Warning}
        onClose={() => setWallet75Warning(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#fff3e0', color: '#e65100' }}>
          Wallet Running Low
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <Typography variant="body1" gutterBottom>
            You've used <strong>{wallet75Warning?.percentUsed}%</strong> of your wallet balance for this session.
          </Typography>
          <Typography variant="body1">
            Remaining balance: <strong>₹{wallet75Warning?.balance?.toFixed(0)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Session will end automatically when balance runs out. Recharge now to continue uninterrupted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWallet75Warning(null)}>Continue</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setWallet75Warning(null);
              setShowRechargeModal(true);
            }}
          >
            Recharge Wallet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connection timeout dialog (PRD-010) */}
      <Dialog
        open={connectionTimedOut}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828' }}>
          Connection Failed
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <Typography variant="body1" gutterBottom>
            Unable to establish a {callType === 'video' ? 'video' : 'voice'} call connection after 45 seconds.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This could be due to network issues or the call service being temporarily unavailable. Please try again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={handleConnectionCancel}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline recharge modal */}
      <RechargeModal
        show={showRechargeModal}
        onHide={() => {
          setShowRechargeModal(false);
          setLowBalanceWarning(false);
          setSessionEndingWarning(false);
        }}
      />

      <RatingModal
        open={showRatingModal}
        onClose={() => {
          setShowRatingModal(false);
          navigate('/dashboard', { state: { ended: true } });
        }}
        astrologerId={currentConsultation?.astrologer_id}
        consultationId={parseInt(consultationId)}
        astrologerName={astrologer?.name || currentConsultation?.astrologer?.name}
      />

      {/* Pre-call DOB collection modal */}
      <PreCallDobModal
        open={showDobModal}
        onClose={() => {
          setShowDobModal(false);
          setPendingCallStart(false);
        }}
        onSubmit={(dobData) => {
          setShowDobModal(false);
          setPendingCallStart(false);
          // Call proceeds — DOB saved to profile
          startCall(consultationId, callType, dobData);
        }}
      />

      {/* Vedic tools overlay for astrologer — user profile, tools, history */}
      <VedicToolsOverlay
        open={showVedicOverlay}
        onClose={() => setShowVedicOverlay(false)}
        consultationId={consultationId}
      />
    </Box>
  );
};

export default ChatWithAstrologerEnhanced;
